/**
 * GameConfig - Centralized configuration for game constants
 * 
 * All physics, camera, player, and timing constants in one place.
 * Makes the game easy to tune and modify.
 * 
 * @module configs/GameConfig
 */

/**
 * Storage keys for persistence
 */
export const STORAGE_KEYS = {
    SETTINGS: 'bigblueball_settings',
    SAVE_DATA: 'bigblueball_save',
    HIGH_SCORES: 'bigblueball_scores'
};

/**
 * Physics configuration for Matter.js
 */
export const PHYSICS = {
    // Global
    GRAVITY: 1,
    FIXED_TIMESTEP: 1000 / 60,
    
    // Solver iterations
    POSITION_ITERATIONS: 10,
    VELOCITY_ITERATIONS: 8,
    CONSTRAINT_ITERATIONS: 4,
    
    // Player body
    PLAYER: {
        RADIUS: 20,
        RESTITUTION: 0,
        FRICTION: 0.03,
        FRICTION_STATIC: 0.02,
        FRICTION_AIR: 0.004,
        DENSITY: 0.0035,
        SLOP: 0.005
    },
    
    // Platform defaults
    PLATFORM: {
        FRICTION: 0.6,
        FRICTION_STATIC: 0.5,
        RESTITUTION: 0,
        CHAMFER_RADIUS: 2,
        SLOP: 0.01
    },
    
    // Box (puzzle element) - realistic physics for grab-and-throw mechanic
    BOX: {
        SIZE: 50,
        FRICTION: 0.2,
        FRICTION_STATIC: 0.2,
        FRICTION_AIR: 0.03,
        RESTITUTION: 0.35,
        DENSITY: 0.004,
        CHAMFER_RADIUS: 3
    },
    
    // Magnetic pull mechanics - smooth grab-and-release
    MAGNETIC: {
        PULL_RANGE: 450,
        PULL_FORCE: 0.09,
        MAX_VELOCITY: 22,
        STABILIZE_THRESHOLD: 25
    },
    
    // Trampoline bounce
    TRAMPOLINE: {
        BOUNCE_VELOCITY: -26
    }
};

/**
 * Player movement and abilities
 */
export const PLAYER_CONFIG = {
    // Movement
    MOVE_FORCE: 0.0035,
    MAX_VELOCITY: 13,
    AIR_CONTROL: 0.7,
    
    // Jumping
    JUMP_FORCE: 0.06,
    DOUBLE_JUMP_FORCE: 0.052,
    MAX_JUMPS: 2,
    COYOTE_TIME: 100,  // ms
    
    // Visual
    SPRITE_RADIUS: 22,
    HIGHLIGHT_OFFSET: { x: -5, y: -5 },
    HIGHLIGHT_RADIUS: 8,
    SHADOW_OFFSET_Y: 25,
    SHADOW_SIZE: { w: 30, h: 10 }
};

/**
 * Dash ability tuning
 */
export const DASH = {
    SPEED: 22,
    DURATION: 140,
    COOLDOWN: 500
};

/**
 * Camera configuration
 */
export const CAMERA = {
    // Follow smoothing (0-1, lower = smoother)
    LERP_X: 0.08,
    LERP_Y: 0.08,
    
    // Lead effect (camera looks ahead)
    LEAD_STRENGTH: 50,
    LEAD_LERP: 0.05,
    LEAD_X_MULTIPLIER: 3,
    LEAD_Y_MULTIPLIER: 1.5,
    
    // Zoom
    DEFAULT_ZOOM: 1.0,
    ZOOM_EFFECT_AMOUNT: 1.02
};

/**
 * Visual effects timing
 */
export const EFFECTS = {
    // Screen shake
    SHAKE_DURATION: {
        LIGHT: 50,
        MEDIUM: 80,
        HEAVY: 200
    },
    SHAKE_INTENSITY: {
        LIGHT: 0.003,
        MEDIUM: 0.005,
        HEAVY: 0.01
    },
    
    // Particles
    JUMP_PARTICLES: 5,
    DOUBLE_JUMP_PARTICLES: 8,
    DEATH_PARTICLES: 16,
    COLLECT_PARTICLES: 12,
    LANDING_DUST_MAX: 12,
    
    // Timings (ms)
    FADE_IN: 500,
    FADE_OUT: 300,
    WIN_FADE: 800,
    DEATH_DELAY: 500
};

/**
 * Game timing
 */
export const TIMING = {
    // Portal cooldown (prevent rapid teleportation)
    PORTAL_COOLDOWN: 1000,
    
    // Timer update interval
    TIMER_INTERVAL: 10,
    
    // Movement detection threshold
    MOVE_THRESHOLD: 5
};

/**
 * UI configuration
 */
export const UI = {
    // HUD
    CONTROLS_HINT_DURATION: 6000,
    
    // Animations
    ANIMATE_IN_DURATION: 500,
    BUTTON_SCALE_HOVER: 1.05,
    
    // Colors (duplicates UIDesignTokens for convenience)
    COLORS: {
        PRIMARY: 0x007AFF,
        SUCCESS: 0x34C759,
        DANGER: 0xFF3B30,
        WARNING: 0xFF9500,
        STAR: 0xFFD700
    }
};

/**
 * Game dimensions
 */
export const SCREEN = {
    WIDTH: 1280,
    HEIGHT: 720,
    BACKGROUND_COLOR: '#1a1a2e'
};

/**
 * Render settings
 */
export const RENDER = {
    ANTIALIAS: true,
    PIXEL_ART: false,
    ROUND_PIXELS: false,
    BATCH_SIZE: 4096,
    TARGET_FPS: 60,
    MIN_FPS: 30
};

export default {
    STORAGE_KEYS,
    PHYSICS,
    PLAYER_CONFIG,
    CAMERA,
    EFFECTS,
    TIMING,
    UI,
    SCREEN,
    RENDER
};
